import React, { useRef, useMemo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Float, MeshDistortMaterial, Icosahedron, TorusKnot, Environment } from "@react-three/drei";
import * as THREE from "three";

function Particles({ count = 260 }) {
  const ref = useRef();
  const positions = useMemo(() => {
    const arr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      arr[i * 3] = (Math.random() - 0.5) * 22;
      arr[i * 3 + 1] = (Math.random() - 0.5) * 14;
      arr[i * 3 + 2] = (Math.random() - 0.5) * 12;
    }
    return arr;
  }, [count]);

  useFrame((state) => {
    if (ref.current) {
      ref.current.rotation.y = state.clock.elapsedTime * 0.03;
    }
  });

  return (
    <points ref={ref}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" count={count} array={positions} itemSize={3} />
      </bufferGeometry>
      <pointsMaterial size={0.045} color="#d4ff4e" transparent opacity={0.55} sizeAttenuation />
    </points>
  );
}

function Blob({ mouse }) {
  const ref = useRef();
  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.elapsedTime;
    ref.current.rotation.x = t * 0.15 + mouse.current.y * 0.3;
    ref.current.rotation.y = t * 0.2 + mouse.current.x * 0.4;
  });
  return (
    <Float speed={1.4} rotationIntensity={0.6} floatIntensity={1.1}>
      <Icosahedron ref={ref} args={[1.65, 8]} position={[0, 0, 0]}>
        <MeshDistortMaterial
          color="#c7c7c2"
          roughness={0.15}
          metalness={0.9}
          distort={0.38}
          speed={1.6}
        />
      </Icosahedron>
    </Float>
  );
}

function Knot({ position, scale, color }) {
  const ref = useRef();
  useFrame((state) => {
    if (ref.current) {
      ref.current.rotation.x = state.clock.elapsedTime * 0.4;
      ref.current.rotation.z = state.clock.elapsedTime * 0.25;
    }
  });
  return (
    <Float speed={2} rotationIntensity={1} floatIntensity={2}>
      <TorusKnot ref={ref} args={[0.4, 0.13, 120, 16]} position={position} scale={scale}>
        <meshStandardMaterial color={color} roughness={0.2} metalness={0.6} wireframe />
      </TorusKnot>
    </Float>
  );
}

function Rig({ mouse }) {
  useFrame((state) => {
    state.camera.position.x += (mouse.current.x * 1.2 - state.camera.position.x) * 0.04;
    state.camera.position.y += (mouse.current.y * 0.8 - state.camera.position.y) * 0.04;
    state.camera.lookAt(0, 0, 0);
  });
  return null;
}

export default function Scene3D() {
  const mouse = useRef({ x: 0, y: 0 });

  const onMove = (e) => {
    mouse.current.x = (e.clientX / window.innerWidth) * 2 - 1;
    mouse.current.y = -((e.clientY / window.innerHeight) * 2 - 1);
  };

  return (
    <div className="absolute inset-0" onMouseMove={onMove}>
      <Canvas camera={{ position: [0, 0, 6], fov: 45 }} dpr={[1, 2]} gl={{ antialias: true, alpha: true }}>
        <ambientLight intensity={0.6} />
        <directionalLight position={[5, 5, 5]} intensity={1.2} />
        <pointLight position={[-5, -3, 2]} intensity={2} color="#d4ff4e" />
        <Blob mouse={mouse} />
        <Knot position={[2.8, 1.3, -1]} scale={1.1} color="#d4ff4e" />
        <Knot position={[-3, -1.2, -1.5]} scale={0.9} color="#7ee7ff" />
        <Particles />
        <Rig mouse={mouse} />
        <Environment preset="city" />
      </Canvas>
    </div>
  );
}
